// MediaDashboard.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";

interface Event {
    eventID: number;
    eventName: string;
}

interface FunctionDto {
    functionId: number;
    functionName: string;
}

interface ApplicationStatusDto {
    id: number;
    status: "Pending" | "Approved" | "Denied";
    functionId: number;
    note: string;
    eventId: number;
}

const MediaDashboard: React.FC = () => {
    const { eventId } = useParams();
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const userId = Number(sessionStorage.getItem("userId"));
    const username = sessionStorage.getItem("username");
    const email = sessionStorage.getItem("userEmail");

    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [functions, setFunctions] = useState<FunctionDto[]>([]);
    const [selectedFunction, setSelectedFunction] = useState<number | null>(null);
    const [note, setNote] = useState("");
    const [applications, setApplications] = useState<ApplicationStatusDto[]>([]);
    const [message, setMessage] = useState<string>("");

    // Load all events (keep it, in case it's used elsewhere)
    useEffect(() => {
        fetch(`${apiUrl}/api/events`)
            .then(res => res.json())
            .then(setEvents)
            .catch(err => console.error(err));
    }, []);

    // Set initial event from URL param
    useEffect(() => {
        if (eventId) setSelectedEventId(Number(eventId));
    }, [eventId]);

    // Load functions and user's applications for selected event
    useEffect(() => {
        if (!selectedEventId || !userId) return;

        const loadData = async () => {
            try {
                // Fetch functions
                const res = await fetch(`${apiUrl}/api/events/${selectedEventId}/media-data`);
                const data = await res.json();
                setFunctions(data.functions || []);

                // Fetch user applications
                const appRes = await fetch(
                    `${apiUrl}/api/media-applications/${selectedEventId}/my-application?userId=${userId}`
                );
                const appData = await appRes.json();
                console.log("Fetched applications:", appData); // debug
                setApplications(Array.isArray(appData) ? appData : []);
            } catch (err) {
                console.error(err);
                setApplications([]);
            }
        };

        loadData();
    }, [selectedEventId, userId]);

    // Handle submitting a new application
    const handleApply = async () => {
        if (!selectedEventId || !selectedFunction) return;

        try {
            await fetch(`${apiUrl}/api/media-applications/${selectedEventId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId,
                    functionId: selectedFunction,
                    note
                })
            });

            // Reload all applications after submission
            const appRes = await fetch(
                `${apiUrl}/api/media-applications/${selectedEventId}/my-application?userId=${userId}`
            );

            if (!appRes.ok) {
                setApplications([]);
                return;
            }

            const appData = await appRes.json();

            // Make sure it's always an array
            setApplications(Array.isArray(appData) ? appData : []);
            console.log("Fetched applications:", appData);
            setMessage("Your application has been submitted!");
            setNote("");
            setSelectedFunction(null);
        } catch (err) {
            console.error(err);
            setMessage("Failed to submit application. Please try again.");
        }
    };

    // Counts for badges
    const counts = {
        pending: applications.filter(a => a.status === "Pending").length,
        approved: applications.filter(a => a.status === "Approved").length,
        denied: applications.filter(a => a.status === "Denied").length
    };

    // Determine latest active application (Pending or Approved)
    const activeApplication = applications
        .filter(a => a.status === "Pending" || a.status === "Approved")
        .sort((a, b) => b.id - a.id)[0];

    const inputsDisabled = !!activeApplication;
    let statusMessage = "";
    if (activeApplication?.status === "Pending") statusMessage = "Your application is pending. ⏳";
    if (activeApplication?.status === "Approved") statusMessage = "Your application has been approved! ✅";

    return (
        <div>
            <HeroSectionV2
                titleEn="Media"
                titleDa="Media"
                descriptionEn="Application for media access"
                descriptionDa="Ansøgning til medieadgang"
                imageSrc={Billede1}
            />

            <div style={{ padding: 30, fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif' }}>
                <h2>User Info</h2>
                <p><strong>Name:</strong> {username}</p>
                <p><strong>Email:</strong> {email}</p>

                <hr />

                <div className="media-dashboard">
                    {/* LEFT SIDE - APPLY */}
                    <div className="media-box">
                        <h2>Apply for Media Access</h2>
                        <hr />

                        {/* Show current event name */}
                        {selectedEventId && (
                            <p>
                                <strong>Event:</strong>{" "}
                                {events.find(ev => ev.eventID === selectedEventId)?.eventName || "Unknown Event"}
                            </p>
                        )}

                        {selectedEventId && (
                            <>
                                <h3 style={{ marginTop: 20 }}>Select Function</h3>
                                <select
                                    value={selectedFunction ?? ""}
                                    onChange={e => setSelectedFunction(Number(e.target.value))}
                                    disabled={inputsDisabled}
                                >
                                    <option value="">Select Function</option>
                                    {functions.map(f => (
                                        <option key={f.functionId} value={f.functionId}>{f.functionName}</option>
                                    ))}
                                </select>

                                <h3 style={{ marginTop: 20 }}>Note (Optional)</h3>
                                <textarea
                                    rows={4}
                                    className="note-textarea"
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    disabled={inputsDisabled}
                                />

                                <button
                                    disabled={!selectedFunction || inputsDisabled}
                                    onClick={handleApply}
                                    className="submit-btn"
                                >
                                    {inputsDisabled ? "Application Active" : "Submit Application"}
                                </button>

                                {statusMessage && <p className="message-warning">{statusMessage}</p>}
                                {message && <p className="message">{message}</p>}
                            </>
                        )}
                    </div>

                    {/* RIGHT SIDE - APPLICATIONS */}
                    <div className="media-box">
                        <div style={{ display: "flex", gap: 15, marginBottom: 15 }}>
                            <span className="status-badge status-Pending">Pending: {counts.pending}</span>
                            <span className="status-badge status-Approved">Approved: {counts.approved}</span>
                            <span className="status-badge status-Denied">Denied: {counts.denied}</span>
                        </div>

                        <h2>Your Applications</h2>

                        {!selectedEventId && <p>No event selected.</p>}
                        {selectedEventId && applications.length === 0 && <p>No applications for this event yet.</p>}

                        {applications.length > 0 && (
                            <div className="applications-grid">
                                {applications.map(app => {
                                    const functionName =
                                        functions.find(f => f.functionId === app.functionId)?.functionName || "Unknown";

                                    return (
                                        <div key={app.id} className={`application-card status-${app.status}`}>
                                            <div className="card-header">
                                                <h4>{functionName}</h4>
                                                <span className={`status-badge status-${app.status}`}>
                                                    {app.status}
                                                </span>
                                            </div>

                                            {app.note && (
                                                <div className="note-box">
                                                    <strong>Note:</strong>
                                                    <p>{app.note}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MediaDashboard;